import apiClient from "@/clients/apiClient";
import type { ApiResponse } from "@/utils/ApiResponse";

export interface ReconciliacionPendientesItem {
  pedidoId: string;
  commerceOrder: string;
  ok: boolean;
  accion?: string;
  error?: string;
}

export interface ReconciliacionPendientesIzipayResponse {
  limit: number;
  candidatosEnBd: number;
  consultados: number;
  marcadoPagado: number;
  cancelados: number;
  errores: number;
  items: ReconciliacionPendientesItem[];
}

/** Resultado acumulado de varios `POST /izipay/reconciliar/pendientes` seguidos. */
export interface ReconciliacionPendientesIzipayAcumulada {
  limitPorLote: number;
  lotesEjecutados: number;
  consultadosTotal: number;
  marcadoPagado: number;
  cancelados: number;
  errores: number;
  /** Última respuesta del backend (útil para depuración). */
  ultimoLote: ReconciliacionPendientesIzipayResponse | null;
}

export type ReconciliacionIzipayLoteProgreso = {
  loteIndex: number;
  candidatosEnBd: number;
};

function parseReconciliarPendientesIzipayPayload(
  data:
    | ReconciliacionPendientesIzipayResponse
    | { data: ReconciliacionPendientesIzipayResponse }
): ReconciliacionPendientesIzipayResponse {
  if (data && typeof data === "object" && "data" in data && (data as { data: unknown }).data) {
    return (data as { data: ReconciliacionPendientesIzipayResponse }).data;
  }
  return data as ReconciliacionPendientesIzipayResponse;
}

/**
 * Admin: sincroniza en lote pedidos «Pendiente» con `commerceOrderExterno` contra Izipay/Lyra
 * (`Order/Get` + misma lógica que IPN/client-answer). Útil si el webhook se perdió.
 */
export async function ReconciliarPendientesIzipayAction(
  limit?: number
): Promise<ApiResponse<ReconciliacionPendientesIzipayResponse>> {
  const { data } = await apiClient.post<
    ReconciliacionPendientesIzipayResponse | { data: ReconciliacionPendientesIzipayResponse }
  >("/izipay/reconciliar/pendientes", limit != null ? { limit } : {});

  const payload = parseReconciliarPendientesIzipayPayload(
    data as
      | ReconciliacionPendientesIzipayResponse
      | { data: ReconciliacionPendientesIzipayResponse }
  );

  return {
    status: 200,
    data: payload,
    message: "Sincronización masiva Izipay ejecutada",
  };
}

const MAX_LOTES_SEGURIDAD = 200;

/**
 * Repite el endpoint en lotes de `limitPorLote` (máx. 100 en backend) hasta agotar candidatos
 * o hasta un lote completo sin cambios a Pagado/Cancelado (evita bucle si la pasarela sigue «pendiente»).
 */
export async function ReconciliarPendientesIzipayPorLotesAction(
  limitPorLote: number,
  onProgress?: (p: ReconciliacionIzipayLoteProgreso) => void
): Promise<ApiResponse<ReconciliacionPendientesIzipayAcumulada>> {
  const cap = Math.min(100, Math.max(1, Math.floor(limitPorLote)));

  let lotesEjecutados = 0;
  let consultadosTotal = 0;
  let marcadoPagado = 0;
  let cancelados = 0;
  let errores = 0;
  let ultimoLote: ReconciliacionPendientesIzipayResponse | null = null;

  while (lotesEjecutados < MAX_LOTES_SEGURIDAD) {
    const res = await ReconciliarPendientesIzipayAction(cap);
    const d = res.data;
    ultimoLote = d;
    lotesEjecutados += 1;
    consultadosTotal += d.consultados;
    marcadoPagado += d.marcadoPagado;
    cancelados += d.cancelados;
    errores += d.errores;

    onProgress?.({ loteIndex: lotesEjecutados, candidatosEnBd: d.candidatosEnBd });

    if (d.candidatosEnBd === 0 || d.consultados === 0) {
      break;
    }

    if (d.candidatosEnBd < cap) {
      break;
    }

    const huboCambioEstado = d.marcadoPagado > 0 || d.cancelados > 0;
    if (!huboCambioEstado) {
      break;
    }

    await new Promise((r) => setTimeout(r, 280));
  }

  return {
    status: 200,
    data: {
      limitPorLote: cap,
      lotesEjecutados,
      consultadosTotal,
      marcadoPagado,
      cancelados,
      errores,
      ultimoLote,
    },
    message: "Sincronización Izipay por lotes ejecutada",
  };
}
